import React, { useState } from 'react';
import { FileCode, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard, ToolActionButtons } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoMarkdown = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [markdownText, setMarkdownText] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    // Convert text to markdown format
    const convertToMarkdown = (text, pageNum) => {
        // Basic markdown conversion
        let markdown = '';
        
        // Add page header
        if (pageNum > 1) {
            markdown += `\n\n---\n\n`;
        }
        markdown += `## Page ${pageNum}\n\n`;

        // Split text into paragraphs (double newlines)
        const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
        
        paragraphs.forEach(paragraph => {
            const trimmed = paragraph.trim();
            if (!trimmed) return;

            // Check if it looks like a heading (short line, all caps, or ends with colon)
            if (trimmed.length < 100 && (trimmed === trimmed.toUpperCase() || trimmed.endsWith(':'))) {
                markdown += `### ${trimmed}\n\n`;
            } else {
                // Regular paragraph
                markdown += `${trimmed}\n\n`;
            }
        });

        return markdown;
    };

    const extractToMarkdown = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            let allMarkdown = `# ${file.name.replace(/\.pdf$/i, '')}\n\n`;
            allMarkdown += `*Converted from PDF on ${new Date().toLocaleDateString()}*\n\n`;

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();

                // Extract text items with their positions for better formatting
                const items = textContent.items;
                let pageText = '';
                let currentY = null;
                let currentLine = '';

                items.forEach((item, index) => {
                    const y = item.transform[5]; // Y position
                    
                    // New line if Y position changed significantly
                    if (currentY !== null && Math.abs(y - currentY) > 5) {
                        if (currentLine.trim()) {
                            pageText += currentLine.trim() + ' ';
                        }
                        currentLine = item.str;
                    } else {
                        currentLine += item.str;
                    }
                    
                    currentY = y;
                });

                // Add last line
                if (currentLine.trim()) {
                    pageText += currentLine.trim();
                }

                // Convert to markdown
                const pageMarkdown = convertToMarkdown(pageText, pageNum);
                allMarkdown += pageMarkdown;

                setProgress(10 + ((pageNum / pdf.numPages) * 80));
            }

            setMarkdownText(allMarkdown);
            setProgress(100);

            toast.success('Markdown extracted successfully!');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to extract markdown: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const downloadMarkdown = () => {
        const blob = new Blob([markdownText], { type: 'text/markdown' });
        const fileName = file.name.replace(/\.pdf$/i, '');
        saveAs(blob, `${fileName}.md`);
        toast.success('Markdown file downloaded!');
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(markdownText);
            toast.success('Markdown copied to clipboard!');
        } catch (error) {
            toast.error('Failed to copy to clipboard');
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileCode size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to Markdown
                </h1>
                <p className="tool-description">
                    Convert PDF documents to Markdown format. Extract text content and structure it as Markdown for easy editing and version control.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { 
                            setFile(null); 
                            setPageCount(0); 
                            setMarkdownText(''); 
                        }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {isProcessing && (
                        <ProgressBar value={progress} striped showLabel />
                    )}

                    {!markdownText && (
                        <div className="button-group">
                            <Button variant="primary" size="lg" onClick={extractToMarkdown} disabled={isProcessing} loading={isProcessing} fullWidth>
                                <FileCode size={20} />
                                Convert to Markdown
                            </Button>
                        </div>
                    )}

                    {markdownText && (
                        <>
                            <Card>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)' }}>
                                        Markdown Content
                                    </h3>
                                    <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                        <Button variant="secondary" size="sm" onClick={copyToClipboard}>
                                            Copy
                                        </Button>
                                        <Button variant="secondary" size="sm" onClick={downloadMarkdown}>
                                            <Download size={16} />
                                            Download MD
                                        </Button>
                                    </div>
                                </div>
                                <div style={{
                                    maxHeight: '500px',
                                    overflowY: 'auto',
                                    padding: 'var(--spacing-4)',
                                    backgroundColor: 'var(--color-surface-elevated)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--text-sm)',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.6,
                                    border: '1px solid var(--color-border)'
                                }}>
                                    {markdownText}
                                </div>
                            </Card>

                            <div className="button-group">
                                <Button variant="primary" size="lg" onClick={downloadMarkdown} fullWidth>
                                    <Download size={20} />
                                    Download as Markdown File
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}

            <NoteCard variant="info">
                <strong>Note:</strong> This tool extracts text from PDFs and converts it to Markdown format. Complex layouts, images, and tables may not be perfectly preserved. For best results, use PDFs with clear text content.
            </NoteCard>
        </div>
    );
};

export default PDFtoMarkdown;






